const root = state => state.lessonContentViewer;

export const selectViewerLoading = state => root(state).loading;
export const selectViewerError = state => root(state).error;
export const selectViewerModel = state => root(state).viewerModel;
export const selectCurrentPhase = state => root(state).phase;
export const selectActiveTermIndex = state => root(state).activeTermIndex;
export const selectActiveQuestionIndex = state => root(state).activeQuestionIndex;
export const selectShowDevTools = state => root(state).showDevTools;
export const selectTutorChatOpen = state => root(state).tutorChatOpen;
export const selectTutorialViewed = state => root(state).tutorialViewed;

export const selectMetadata = state => root(state).viewerModel?.metadata || null;

export const selectActiveTerm = state => {
  const model = root(state).viewerModel;
  const idx = root(state).activeTermIndex;
  return model?.terms?.[idx] || null;
};

export const selectActiveTermCard = state => {
  const term = selectActiveTerm(state);
  if (!term) return null;
  return {
    term: term.term,
    definition: term.definition,
    exampleSentence: term.exampleSentence,
  };
};

export const selectActiveWorkbook = state => {
  const term = selectActiveTerm(state);
  return term?.workbook || null;
};

export const selectWorkbookState = state => ({
  ready: root(state).workbookReady,
  done: root(state).workbookDone,
  submitted: root(state).workbookSubmitted,
  selectedOption: root(state).workbookSelectedOption,
});

export const selectActiveKCQuestions = state => {
  const term = selectActiveTerm(state);
  return term?.knowledgeCheck?.questions || [];
};

export const selectActiveKCQuestion = state => {
  const questions = selectActiveKCQuestions(state);
  const idx = root(state).activeQuestionIndex;
  return questions[idx] || null;
};

export const selectKCAnswers = state => root(state).kcAnswers;

export const selectKCScore = state => {
  const answers = root(state).kcAnswers;
  return {
    correct: answers.filter(a => a.correct).length,
    total: answers.length,
  };
};

export const selectMasteryQuestions = state =>
  root(state).viewerModel?.mastery?.questions || [];

export const selectActiveMasteryQuestion = state => {
  const questions = selectMasteryQuestions(state);
  const idx = root(state).activeQuestionIndex;
  return questions[idx] || null;
};

export const selectMasteryAnswers = state => root(state).masteryAnswers;

export const selectMasteryScore = state => {
  const answers = root(state).masteryAnswers;
  const threshold = root(state).viewerModel?.mastery?.passingThreshold || 0.8;
  const correct = answers.filter(a => a.correct).length;
  const total = answers.length;
  const ratio = total > 0 ? correct / total : 0;
  return {
    correct,
    total,
    passed: ratio >= threshold,
    ratio,
    threshold,
  };
};

export const selectMasteryComplete = state => root(state).masteryComplete;

export const selectFrontloadingData = state => {
  const model = root(state).viewerModel;
  if (!model) return null;
  return {
    tutorScript: model.frontloading.tutorScript,
    termOrder: model.frontloading.termOrder,
    terms: model.terms,
    estimatedDurationMinutes: model.metadata.estimatedDurationMinutes,
    topic: model.metadata.topic,
  };
};

export const selectProgressTrackerData = state => {
  const model = root(state).viewerModel;
  const s = root(state);
  if (!model) return null;

  const phase = s.phase;
  const introComplete = phase !== 'tutorial' && phase !== 'frontloading';
  const masteryReached = ['mastery', 'results'].includes(phase);

  let activeSubPhase = null;
  if (['learning'].includes(phase)) activeSubPhase = 'learning';
  else if (
    ['workbook_ready', 'workbook_exercise', 'workbook_options'].includes(phase)
  )
    activeSubPhase = 'workbook';
  else if (['knowledge_check', 'knowledge_check_summary'].includes(phase))
    activeSubPhase = 'knowledge_check';

  const windowStart = Math.max(0, s.activeTermIndex);

  return {
    terms: model.terms.map(t => ({
      term: t.term,
      completed: false,
    })),
    activeTermIndex: s.activeTermIndex,
    activeSubPhase,
    introComplete,
    masteryReached,
    windowStart,
  };
};

export const selectTutorText = state => {
  const s = root(state);
  const term = selectActiveTerm(state);
  const model = s.viewerModel;

  switch (s.phase) {
    case 'tutorial':
      return 'Welcome! Let me show you how this lesson works.';
    case 'frontloading':
      return model?.frontloading?.tutorScript || '';
    case 'learning':
      return term?.learning?.tutorScript || '';
    case 'workbook_ready':
      return s.workbookReady
        ? term?.workbook?.tutorReadExercise || ''
        : term?.workbook?.tutorOpenWorkbook || '';
    case 'workbook_exercise':
      return term?.workbook?.tutorReadExercise || '';
    case 'workbook_options':
      if (s.workbookSubmitted && s.workbookSelectedOption != null) {
        const feedback = term?.workbook?.optionFeedback || [];
        return feedback[s.workbookSelectedOption] || '';
      }
      return term?.workbook?.tutorPresentOptions || '';
    case 'knowledge_check':
      if (s.kcAnswers.length === 0) {
        return (
          term?.knowledgeCheck?.tutorTransition ||
          term?.workbook?.tutorTransition ||
          'Let\'s check what you\'ve learned!'
        );
      }
      return '';
    case 'knowledge_check_summary': {
      const score = selectKCScore(state);
      return `Great work! You got ${score.correct} out of ${score.total} correct. Let's keep going!`;
    }
    case 'mastery':
      return 'Time for the mastery test! Show me what you\'ve learned.';
    case 'results': {
      const mScore = selectMasteryScore(state);
      return mScore.passed
        ? 'Excellent work! You\'ve mastered this lesson!'
        : 'Good effort! Keep practicing to improve your score.';
    }
    default:
      return '';
  }
};
